import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import emailjs from 'emailjs-com';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/PopupWindow.css';
import { Check } from 'lucide-react';

function PopupWindow({ isOpen, onClose, selectedWorkspaceId }) {
  const { language } = useLanguage();
  const [message, setMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [authUserId, setAuthUserId] = useState(null);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setAuthUserId(user.id);
          setEmail(user.email);

          // Fetch user profile data
          const { data: profileData, error } = await supabase
            .from('user_profiles')
            .select('fullname')
            .eq('id', user.id)
            .single();

          if (error) throw error;

          if (profileData) {
            setFullName(profileData.fullname);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    if (isOpen) {
      fetchUserData();
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    try {
      const templateParams = {
        fullname: isAnonymous ? 'Anonymous' : fullName,
        id: isAnonymous ? 'Anonymous' : authUserId,
        message: message,
        email: isAnonymous ? 'anonymous@anonymous.com' : email,
        workspace_id: selectedWorkspaceId || 'No workspace selected'
      };

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID_POPUP,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      // Clear form and close
      setMessage('');
      setIsAnonymous(false);
      onClose();
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  };

  const handleToggleAnonymous = () => {
    setIsAnonymous(!isAnonymous);
    if (!isAnonymous) {
      // Reset user data when switching to anonymous
      setAuthUserId(null);
      setEmail(null);
    } else {
      // Restore user data when switching back
      const restoreUserData = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            setAuthUserId(user.id);
            setEmail(user.email);

            // Fetch user profile data
            const { data: profileData, error } = await supabase
              .from('user_profiles')
              .select('fullname')
              .eq('id', user.id)
              .single();

            if (error) throw error;

            if (profileData) {
              setFullName(profileData.fullname);
            }
          }
        } catch (error) {
          console.error('Error restoring user data:', error);
        }
      };
      restoreUserData();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <h2>{language === 'en' ? 'Share feedback with us' : 'Teilen Sie uns Ihr Feedback mit'}</h2>
        <div className="popup-body">
          <h3>{language === 'en' ? 'Please provide some details' : 'Bitte geben Sie einige Details an'}</h3>
          <textarea
            className="feedback-textarea"
            placeholder={language === 'en' ? 'Add your feedback here' : 'Geben Sie hier Ihr Feedback ein'}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <p className="popup-info">
            {language === 'en' ? (
              <>
                When you submit this report, the above message will be sent to the Morrow Collective team. By default,
                it will contain the name of your organization, your email address and the current page URL.
                However, you can also choose to send us the feedback anonymously.
              </>
            ) : (
              <>
                Wenn Sie diesen Bericht absenden, wird die obige Nachricht an das Morrow Collective Team gesendet. Standardmäßig
                enthält es den Namen Ihres Unternehmens, Ihre E-Mail-Adresse und die aktuelle Seiten-URL.
                Sie können uns das Feedback jedoch auch anonym zusenden.
              </>
            )}
          </p>
          <div className="anonymous-toggle">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={handleToggleAnonymous}
              />
              <span className="toggle-slider"></span>
            </label>
            <span>{language === 'en' ? 'Share anonymously' : 'Anonym teilen'}</span>
          </div>
          <div className="popup-buttons">
            <button className="cancel-button" onClick={onClose}>
              {language === 'en' ? 'Cancel' : 'Abbrechen'}
            </button>
            <button className="submit-button" onClick={handleSubmit}>
              <Check size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {language === 'en' ? 'Submit' : 'Absenden'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PopupWindow; 